import {
  CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME,
  CustomAttributes,
} from "models/generateCustomAttributesSchema";
import moment from "moment";
import { createLabelFunction } from "./table-utils";
import { formatDateValue, toUnixEpochTime } from "./date-format";
import { formatMonetaryValue } from "pages/audit-overview/definitions";
import {
  buildEntityCompositeKey,
  parseEntityCompositeKey,
} from "features/ui-content/utils";

type customAttributesFormControlledDataMap = Record<string, any>;
const DEFAULT_DATE = "0";

// Parse simple comma-separated values
export const parseCommaSeparated = (value?: string): string[] =>
  value?.split(",").filter((item) => item.trim()) || [];

// Parse comma-separated values that may contain parentheses
export const parseCommaSeparatedWithParentheses = (value: string): string[] => {
  if (!value) return [];
  const result: string[] = [];
  let current = "";
  let parenthesesLevel = 0;
  for (let i = 0; i < value.length; i++) {
    const char = value[i];
    if (char === "(") {
      parenthesesLevel++;
      current += char;
    } else if (char === ")") {
      parenthesesLevel--;
      current += char;
    } else if (char === "," && parenthesesLevel === 0) {
      const trimmed = current.trim();
      if (trimmed) {
        result.push(trimmed);
      }
      current = "";
    } else {
      current += char;
    }
  }
  const trimmed = current.trim();
  if (trimmed) {
    result.push(trimmed);
  }
  return result;
};

// This formatter is defined by team. Datepick will pass value as format of "YYYY-MM-DD" to controller, but controller can pass any formated value to datepicker to render
export const formatDateString = (date: string, formatter: string) => {
  return moment.utc(Number(date) * 1000).format(formatter);
};

// this is for building the data sent to backend: we only want to show the value when it has valid value
export const generateDynamicAttributesDataMap = (
  customAttributesSchemaData: CustomAttributes,
  data: any
): Record<string, string> => {
  // Parse entity composite key to extract just the entity name
  if (data?.entity && typeof data.entity === "string") {
    data.entity = parseEntityCompositeKey(data.entity).name;
  }

  const recordMap = customAttributesSchemaData.reduce((acc, attribute) => {
    const { attributeName, formFieldType } = attribute;
    const value = data[attributeName];
    if (value) {
      if (formFieldType === "Date") {
        // By default, the datepicker is passed the date value with format of "YYYY-MM-DD" to form controller, we will transform as instant for saving
        acc[attributeName] = toUnixEpochTime(value).toString();
      } else if (formFieldType === "Multiselect") {
        if (Array.isArray(value) && value.length > 0) {
          acc[attributeName] = value.join(", ");
        }
      } else {
        acc[attributeName] = value.toString();
      }
    } else {
      if (formFieldType === "Checkbox") {
        acc[attributeName] = "false";
      }
    }

    return acc;
  }, {} as Record<string, string>);
  return recordMap;
};

export const transformCustomAttributesDataMap = (
  response: any,
  customAttributesDefinition: CustomAttributes,
  teamDateFormatter: string
) => {
  // if customAttributesDefinition is not present, no need to transform the data response
  if (
    !customAttributesDefinition ||
    !Array.isArray(customAttributesDefinition)
  ) {
    return response;
  }

  // if custom attributes are defined, make a deep copy of response data
  const responseCopy = deepCopy(response);

  /**
   * Parses entity code format: "code" or "code - registration number"
   */
  const parseEntityCode = (entityCode: string) => {
    const parts = entityCode.split(" - ");
    return {
      code: parts[0],
      registrationNumber: parts[1], // undefined if not present
    };
  };

  // Created a customAttributesFormControlledDataMap
  if (responseCopy[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]) {
    // Make a copy of data map to create the form data map
    const customAttributesFormControlledDataMap: customAttributesFormControlledDataMap =
      deepCopy(responseCopy[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]);

    // For form data map, boolean value is used for boolean attributes, date format is enforced for date attributes for both form data map and rendering data map
    customAttributesDefinition.forEach((attribute) => {
      const field = attribute.attributeName;
      const fieldValue = customAttributesFormControlledDataMap[field];
      if (fieldValue) {
        switch (attribute.inputValueType) {
          case "boolean":
            customAttributesFormControlledDataMap[field] =
              fieldValue.toLowerCase() === "true";
            break;
          case "stringArray":
            const stringArray = parseCommaSeparatedWithParentheses(fieldValue);
            customAttributesFormControlledDataMap[field] = stringArray;
            break;
          case "date":
            const formattedDataString = formatDateString(
              fieldValue,
              teamDateFormatter
            );
            customAttributesFormControlledDataMap[field] = formattedDataString;
            responseCopy[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME][field] =
              formattedDataString;
            break;
          default:
            customAttributesFormControlledDataMap[field] = fieldValue;
            break;
        }
      }

      // Reconstruct entity composite key for edit mode using entity name and entityCode
      if (field === "entity" && fieldValue) {
        const entityCode =
          response[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]?.entityCode;

        if (entityCode) {
          const { code, registrationNumber } = parseEntityCode(entityCode);
          customAttributesFormControlledDataMap[field] =
            buildEntityCompositeKey(fieldValue, code, registrationNumber);
        }
      }
    });
    responseCopy[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME] =
      customAttributesFormControlledDataMap;
  } else {
    delete responseCopy[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME];
  }

  // Reconstruct entity composite key for audits
  if (responseCopy.entity && responseCopy.entityCode) {
    const { code, registrationNumber } = parseEntityCode(
      responseCopy.entityCode
    );
    responseCopy.entity = buildEntityCompositeKey(
      responseCopy.entity,
      code,
      registrationNumber
    );
  }

  return responseCopy;
};

export const customFieldValueToString = (
  inputValueType: string | undefined,
  customFieldValue: any,
  currencySign?: string //required for "amount" inputValueType
) => {
  if (inputValueType === "boolean") {
    if (typeof customFieldValue === "boolean") {
      return customFieldValue ? "Yes" : "No";
    } else if (typeof customFieldValue === "string") {
      return customFieldValue.toLowerCase() === "true" ? "Yes" : "No";
    } else {
      return "No";
    }
  }
  if (inputValueType === "date" && customFieldValue != null) {
    return customFieldValue?.replaceAll("-", "/");
  }
  if (inputValueType === "amount" && customFieldValue != null) {
    return formatMonetaryValue(customFieldValue, currencySign!);
  }
  return customFieldValue?.toString() ?? "-";
};

function deepCopy<T>(obj: T): T {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  // Create a new object or array based on the type of the input
  const copy = Array.isArray(obj) ? [] : {};

  // Recursively copy the properties or elements
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      (copy as any)[key] = deepCopy((obj as any)[key]);
    }
  }

  return copy as T;
}

export function attributeSchemaToColDef(schema: any) {
  switch (schema.inputValueType) {
    case "date":
    case "number":
      return {
        id: schema.attributeName,
        header: schema.fieldName,
        cell: (item: any) => toColValue(schema, item),
        ariaLabel: createLabelFunction(schema.fieldName),
        sortingField: schema.attributeName,
        sortingComparator: (
          a: {
            customAttributesDataMap: {
              [x: string]: string;
            };
          },
          b: {
            customAttributesDataMap: {
              [x: string]: string;
            };
          }
        ) =>
          Number(a.customAttributesDataMap?.[schema.attributeName] || 0) -
          Number(b.customAttributesDataMap?.[schema.attributeName] || 0),
      };
    default:
      return {
        id: schema.attributeName,
        header: schema.fieldName,
        cell: (item: any) => toColValue(schema, item),
        ariaLabel: createLabelFunction(schema.fieldName),
        sortingField: schema.attributeName,
        sortingComparator: (
          a: {
            customAttributesDataMap: {
              [x: string]: string;
            };
          },
          b: {
            customAttributesDataMap: {
              [x: string]: string;
            };
          }
        ) =>
          (
            a.customAttributesDataMap?.[schema.attributeName] || ""
          ).localeCompare(
            b.customAttributesDataMap?.[schema.attributeName] || ""
          ),
      };
  }
}

export const toColValue = (schema: any, item: any) => {
  if (item?.customAttributesDataMap?.[schema.attributeName] !== undefined) {
    switch (schema.inputValueType) {
      case "date":
        return formatDateValue(
          Number(item.customAttributesDataMap[schema.attributeName])
        );
      default:
        return item.customAttributesDataMap[schema.attributeName];
    }
  }
  return "";
};
